import { Link, useParams } from 'react-router-dom'
import { Faq } from '../components/Faq'
import vs from '../data/vs.json'
import { usePageMeta } from '../lib/usePageMeta'

// Honest "/vs/<competitor>" comparison pages + the /vs index. All copy comes from src/data/vs.json,
// the SAME source the build-time prerender (prerender.vs.mjs) reads, so the hydrated app, the static
// HTML crawlers see, and the FAQPage JSON-LD (injected by <Faq>) render identical text and never drift.
const US = 'SchedulesMCP'

type VsPageData = (typeof vs.pages)[number]

function ComparisonTable({ page }: { page: VsPageData }) {
  return (
    <div className="overflow-x-auto border border-[var(--border-strong)] rounded-2xl">
      <table className="w-full border-collapse text-sm min-w-[560px]">
        <thead>
          <tr className="bg-[var(--surface-1)] border-b border-[var(--border-strong)]">
            <th className="text-left px-4 py-3">&nbsp;</th>
            <th className="text-left px-4 py-3 text-signal font-bold text-[13px]">{US}</th>
            <th className="text-left px-4 py-3 text-[var(--text-secondary)] font-bold text-[13px]">
              {page.columns.them}
            </th>
          </tr>
        </thead>
        <tbody>
          {page.table.map((r) => (
            <tr key={r.feature} className="border-b border-border last:border-0">
              <th
                scope="row"
                className="text-left px-4 py-3.5 align-top font-semibold text-[var(--text-primary)]"
              >
                {r.feature}
              </th>
              <td className="px-4 py-3.5 align-top text-[var(--text-primary)] bg-signal/5">
                {r.us}
              </td>
              <td
                className={`px-4 py-3.5 align-top ${
                  r.them === '—' ? 'text-[var(--text-faint)]' : 'text-[var(--text-secondary)]'
                }`}
              >
                {r.them}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const ctaButtons = (
  <div className="flex flex-wrap gap-3 my-8">
    <Link
      to="/compare"
      className="bg-signal text-on-accent font-bold px-6 py-3 rounded-lg no-underline hover:opacity-90"
    >
      Search a lane free
    </Link>
    <Link
      to="/mcp"
      className="border border-border text-[var(--text-primary)] px-6 py-3 rounded-lg no-underline hover:border-border-strong"
    >
      Add to Claude (MCP)
    </Link>
  </div>
)

function VsIndex() {
  usePageMeta(vs.index.title, vs.index.description)
  return (
    <main className="w-full max-w-4xl mx-auto px-5 sm:px-6 pt-12 pb-16">
      <nav className="text-[13px] text-[var(--text-secondary)] mb-5">
        <Link to="/" className="no-underline text-[var(--text-secondary)] hover:text-signal">
          SchedulesMCP
        </Link>{' '}
        › Compare tools
      </nav>
      <p
        className="m-0 font-mono text-xs uppercase text-accent"
        style={{ letterSpacing: '0.14em' }}
      >
        Comparison
      </p>
      <h1
        className="font-display text-4xl mt-3 mb-4"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        {vs.index.h1}
      </h1>
      <p className="text-[var(--text-secondary)] text-lg leading-relaxed max-w-2xl mb-8">
        {vs.index.intro}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        {vs.pages.map((p) => (
          <Link
            key={p.slug}
            to={`/vs/${p.slug}`}
            className="block bg-surface-0 border border-border rounded-xl p-5 no-underline hover:border-border-strong transition-colors"
          >
            <div className="font-display font-bold text-lg text-[var(--text-primary)] mb-1.5">
              {US} vs {p.competitor}
            </div>
            <div className="text-sm text-[var(--text-secondary)] leading-relaxed">{p.tagline}</div>
            <div className="text-[13px] font-semibold text-signal mt-3">See the comparison →</div>
          </Link>
        ))}
      </div>
      {ctaButtons}
    </main>
  )
}

function VsComparison({ page }: { page: VsPageData }) {
  usePageMeta(page.title, page.description)
  return (
    <main className="w-full max-w-4xl mx-auto px-5 sm:px-6 pt-12 pb-16 overflow-x-hidden">
      <nav className="text-[13px] text-[var(--text-secondary)] mb-5">
        <Link to="/" className="no-underline text-[var(--text-secondary)] hover:text-signal">
          SchedulesMCP
        </Link>{' '}
        ›{' '}
        <Link to="/vs" className="no-underline text-[var(--text-secondary)] hover:text-signal">
          Compare tools
        </Link>{' '}
        › vs {page.competitor}
      </nav>
      <p
        className="m-0 font-mono text-xs uppercase text-accent"
        style={{ letterSpacing: '0.14em' }}
      >
        Head to head
      </p>
      <h1
        className="font-display text-4xl mt-3 mb-4"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        {US} vs {page.competitor}
      </h1>
      <p className="text-[var(--text-secondary)] text-lg leading-relaxed max-w-2xl mb-2">
        {page.intro}
      </p>
      {ctaButtons}

      <h2 className="font-display font-light tracking-[-0.015em] text-2xl mt-10 mb-3">
        {US} vs {page.competitor}, side by side
      </h2>
      <ComparisonTable page={page} />
      <p className="text-xs text-[var(--text-muted)] leading-relaxed mt-2.5">
        "—" means the competitor doesn't publish or offer this, as far as we can tell from public
        sources; "Not published" means it may exist but isn't disclosed. We'd happily correct
        anything out of date.
      </p>

      <h2 className="font-display font-light tracking-[-0.015em] text-2xl mt-11 mb-3">
        Honest about {page.competitor}
      </h2>
      <div className="bg-surface-0 border border-border border-l-[3px] border-l-signal rounded-xl px-5 py-4 text-[var(--text-secondary)] leading-relaxed">
        {page.honest}
      </div>
      {ctaButtons}

      <Faq id={`vs-${page.slug}`} items={page.faqs} heading={`${US} vs ${page.competitor}: FAQ`} />

      <h2 className="font-display font-light tracking-[-0.015em] text-2xl mt-14 mb-3">
        Compare the other tools
      </h2>
      <div className="flex flex-wrap gap-2.5">
        {vs.pages
          .filter((p) => p.slug !== page.slug)
          .map((p) => (
            <Link
              key={p.slug}
              to={`/vs/${p.slug}`}
              className="bg-surface-0 border border-border rounded-full px-3.5 py-1.5 text-[13px] no-underline text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-border-strong"
            >
              vs {p.competitor}
            </Link>
          ))}
        <Link
          to="/vs"
          className="border border-signal/30 rounded-full px-3.5 py-1.5 text-[13px] no-underline text-signal"
        >
          All comparisons →
        </Link>
      </div>
    </main>
  )
}

export function VsPage() {
  const { competitor } = useParams<{ competitor?: string }>()
  if (!competitor) return <VsIndex />
  const page = vs.pages.find((p) => p.slug === competitor)
  if (!page) return <VsIndex />
  return <VsComparison page={page} />
}
